// Xero connection panel (BUILD_SPEC.md §5.9, §12) — status PENDING/CONNECTED/
// ERROR, a Connect button that redirects the browser to Xero's OAuth consent
// screen (GET /xero/connect's `authorizationUrl`), and Disconnect. The whole
// system stays fully functional while PENDING (no Xero app credentials
// configured yet) — this panel says so plainly rather than looking broken.
import { Alert, Button, Group, Loader, Paper, Stack, Text, Title } from '@mantine/core';
import { IconAlertTriangle, IconLink, IconLinkOff, IconPlugConnected } from '@tabler/icons-react';
import { ApiRequestError } from '../../../api/client';
import { ConfirmButton } from '../../../components/ConfirmButton';
import { DocStatusBadge } from '../../../components/DocStatusBadge';
import { useIsAdmin } from '../permissions';
import { useXeroConnect, useXeroConnection, useXeroDisconnect } from '../api';

export function XeroConnectionCard() {
  const isAdmin = useIsAdmin();
  const connectionQuery = useXeroConnection();
  const connect = useXeroConnect();
  const disconnect = useXeroDisconnect();

  function handleConnect() {
    connect.mutate(undefined, {
      onSuccess: (response) => {
        if (response.configured && response.authorizationUrl) {
          window.location.assign(response.authorizationUrl);
        }
      },
    });
  }

  if (connectionQuery.isLoading) {
    return <Loader size="sm" />;
  }

  if (connectionQuery.isError) {
    return (
      <Alert color="red" icon={<IconAlertTriangle size={16} />}>
        {connectionQuery.error instanceof ApiRequestError
          ? connectionQuery.error.message
          : 'Failed to load Xero connection status.'}
      </Alert>
    );
  }

  const connection = connectionQuery.data;

  return (
    <Paper withBorder radius="md" p="lg">
      <Stack gap="sm">
        <Group justify="space-between">
          <Group gap="xs">
            <IconPlugConnected size={20} />
            <Title order={3}>Xero connection</Title>
          </Group>
          <DocStatusBadge status={connection?.status ?? 'PENDING'} domain="xeroConnection" />
        </Group>

        {connection && !connection.configured && (
          <Alert color="gray" variant="light">
            Xero app credentials are not configured for this deployment yet (XERO_CLIENT_ID/SECRET/REDIRECT_URI).
            The rest of the system remains fully functional — connect once Stuart provisions the Xero app.
          </Alert>
        )}

        {connection?.connectedAt && (
          <Text size="sm" c="dimmed">
            Connected {new Date(connection.connectedAt).toLocaleString()}
            {connection.xeroTenantId ? ` · Xero org ${connection.xeroTenantId}` : ''}
          </Text>
        )}
        {connection?.lastError && (
          <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
            Last error: {connection.lastError}
          </Alert>
        )}

        {connect.isError && (
          <Alert color="red" icon={<IconAlertTriangle size={16} />}>
            {connect.error instanceof ApiRequestError ? connect.error.message : 'Failed to start the Xero connect flow.'}
          </Alert>
        )}
        {connect.data && !connect.data.configured && (
          <Alert color="gray" variant="light">
            {connect.data.message}
          </Alert>
        )}
        {disconnect.isError && (
          <Alert color="red" icon={<IconAlertTriangle size={16} />}>
            {disconnect.error instanceof ApiRequestError ? disconnect.error.message : 'Failed to disconnect.'}
          </Alert>
        )}

        {isAdmin ? (
          <Group>
            {connection?.status !== 'CONNECTED' ? (
              <Button leftSection={<IconLink size={16} />} onClick={handleConnect} loading={connect.isPending}>
                Connect to Xero
              </Button>
            ) : (
              <ConfirmButton
                color="red"
                variant="light"
                leftSection={<IconLinkOff size={16} />}
                loading={disconnect.isPending}
                label="Disconnect"
                message="Disconnect Xero? The stored access token is deleted and every screen goes back to needing a fresh Connect + re-authorization before syncing again."
                onConfirm={() => disconnect.mutate()}
              />
            )}
          </Group>
        ) : (
          <Text size="sm" c="dimmed">
            ADMIN role is required to connect or disconnect Xero.
          </Text>
        )}
      </Stack>
    </Paper>
  );
}
